import { AlertTriangle } from 'lucide-react'
import { motion } from 'motion/react'
import { Suspense, use } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { AuthContext } from '@/components/auth/context'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { SevallaLogo } from '@/logos'
import { AuthorizeForm } from '@/routes/authorize/authorize-form'
import { AuthorizeFallback } from '@/routes/authorize/fallback'

export const Authorize = () => {
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code') ?? ''
  const appName = searchParams.get('name') ?? ''
  const callback = searchParams.get('callback') ?? ''
  const { isAuthenticated = false } = use(AuthContext) ?? {}

  if (!isAuthenticated) {
    const returnTo = `/authorize?${searchParams.toString()}`
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} />
  }

  return (
    <div className="bg-muted relative flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="from-primary/20 absolute top-0 right-0 left-0 h-1 bg-gradient-to-r to-transparent" />

      <motion.div
        className="flex w-full max-w-xl flex-col gap-4"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <SevallaLogo className="h-auto w-24 self-center" />

        {code ? (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display flex items-center justify-center gap-2 text-xl">
                Authorize {appName || 'application'}
              </CardTitle>
              <CardDescription>
                Grant {appName || 'this application'} access to your account by
                selecting a company and permission level.
              </CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              <Suspense fallback={<AuthorizeFallback />}>
                <AuthorizeForm
                  code={code}
                  appName={appName}
                  callback={callback}
                />
              </Suspense>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
              <div className="bg-destructive/10 flex size-12 items-center justify-center rounded-full">
                <AlertTriangle className="text-destructive size-6" />
              </div>
              <Badge variant="destructive">Invalid request</Badge>
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  No device code was provided.
                </p>
                <p className="text-muted-foreground text-sm">
                  Please try again from {appName || 'the application'}.
                </p>
              </div>
              <a
                href="/"
                className="text-primary text-sm underline underline-offset-4"
              >
                Return to dashboard
              </a>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  )
}
